import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type {
  ERPConnectionResponse,
  ERPConnectionDetailResponse,
  ERPConnectionPatchRequest,
  ERPConnectionRemovalPreview,
  ERPConnectionRemovalRequest,
  ERPConnectionRemovalResponse,
  ERPConnectionSyncResponse,
  ErpRealmSwitchOptions,
  ErpRealmSwitchResponse,
  TenantERPIntegrationCredentialsRequest,
  TenantERPIntegrationCredentialsResponse,
  QbdInstallSessionResponse,
  QbdPairingClaimRequest,
  QbdPairingClaimResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

export const useGetErpConnection = (enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<ERPConnectionResponse | null>({
    queryKey: ['erp-connection', tenantScope],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<ERPConnectionResponse>(
          '/erp/connection'
        );
        return data;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          return null;
        }

        throw error;
      }
    },
    enabled,
    retry: false, // 404 is expected when no connection exists
  });
};

export const useGetErpConnections = (enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<ERPConnectionDetailResponse[]>({
    queryKey: ['erp-connections', tenantScope],
    queryFn: async () => {
      const { data } = await apiClient.get<ERPConnectionDetailResponse[]>('/erp/connections');
      return data;
    },
    enabled,
  });
};

export const useErpConnectionRemovalPreview = (connectionId?: string) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');
  return useQuery<ERPConnectionRemovalPreview>({
    queryKey: ['erp-connections', tenantScope, connectionId, 'removal-preview'],
    queryFn: async () => {
      const { data } = await apiClient.get<ERPConnectionRemovalPreview>(
        `/erp/connections/${connectionId}/removal-preview`,
      );
      return data;
    },
    enabled: Boolean(connectionId),
  });
};

export const useRemoveErpConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async ({
      connectionId,
      options,
    }: {
      connectionId: string;
      options: ERPConnectionRemovalRequest;
    }) => {
      const { data } = await apiClient.post<ERPConnectionRemovalResponse>(
        `/erp/connections/${connectionId}/remove`,
        options,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({ message: 'ERP connection removed', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to remove ERP connection',
        type: 'error',
      });
    },
  });
};

export const useSyncErpConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { data } = await apiClient.post<ERPConnectionSyncResponse>(
        `/erp/connections/${connectionId}/sync`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['qb-items'] });
      queryClient.invalidateQueries({ queryKey: ['qb-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['erp-field-options'] });
      addNotification({
        message: 'ERP sync submitted',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to submit ERP sync',
        type: 'error',
      });
    },
  });
};

export const useUpdateErpConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      connectionId,
      payload,
    }: {
      connectionId: string;
      payload: ERPConnectionPatchRequest;
    }) => {
      const { data } = await apiClient.patch<ERPConnectionDetailResponse>(
        `/erp/connections/${connectionId}`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
      addNotification({
        message: 'ERP connection updated',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update ERP connection',
        type: 'error',
      });
    },
  });
};

export const useRequestQbdCompanyFileSwitch = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { data } = await apiClient.post<ERPConnectionDetailResponse>(
        `/erp/connections/${connectionId}/quickbooks-desktop/switch-file`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      addNotification({
        message: 'QuickBooks Desktop switch requested. The Windows tray will open the selected file.',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to request QuickBooks Desktop file switch',
        type: 'error',
      });
    },
  });
};

export const usePendingErpRealmSwitch = (enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<ErpRealmSwitchResponse | null>({
    queryKey: ['erp-realm-switch', tenantScope, 'pending'],
    queryFn: async () => {
      const { data } = await apiClient.get<ErpRealmSwitchResponse | null>(
        '/erp/realm-switch/pending'
      );
      return data;
    },
    enabled,
    retry: false,
  });
};

export const useGetAjeraCredentials = (enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<TenantERPIntegrationCredentialsResponse>({
    queryKey: ['erp-credentials', tenantScope, 'ajera'],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantERPIntegrationCredentialsResponse>(
        '/erp/connection/ajera/credentials'
      );
      return data;
    },
    enabled,
    retry: false,
  });
};

export const useUpdateAjeraCredentials = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TenantERPIntegrationCredentialsRequest) => {
      const { data } = await apiClient.put<TenantERPIntegrationCredentialsResponse>(
        '/erp/connection/ajera/credentials',
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-credentials', 'ajera'] });
      queryClient.invalidateQueries({ queryKey: ['erp-credentials'] });
    },
  });
};

export const useExecuteErpRealmSwitch = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      realmSwitchId,
      options,
    }: {
      realmSwitchId: string;
      options: ErpRealmSwitchOptions;
    }) => {
      const { data } = await apiClient.post<ErpRealmSwitchResponse>(
        `/erp/realm-switch/${realmSwitchId}/execute`,
        options
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
      queryClient.invalidateQueries({ queryKey: ['erp-realm-switch', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message: 'Realm switched and records prepared for sync',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to switch realm',
        type: 'error',
      });
    },
  });
};

export const useDismissErpRealmSwitch = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (realmSwitchId: string) => {
      const { data } = await apiClient.post<ErpRealmSwitchResponse>(
        `/erp/realm-switch/${realmSwitchId}/dismiss`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-realm-switch', 'pending'] });
      addNotification({
        message: 'Realm switch dismissed',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to dismiss realm switch',
        type: 'error',
      });
    },
  });
};

export const useStartQbdInstallSession = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<QbdInstallSessionResponse>(
        '/erp/connector/quickbooks-desktop/install-sessions',
      );
      return data;
    },
  });
};

export const useClaimQbdPairing = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      pairingCode,
      payload,
    }: {
      pairingCode: string;
      payload: QbdPairingClaimRequest;
    }) => {
      const normalizedCode = pairingCode.trim().toUpperCase();
      const { data } = await apiClient.post<QbdPairingClaimResponse>(
        `/erp/connection/quickbooks-desktop/pairing/${encodeURIComponent(normalizedCode)}/claim`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
      addNotification({
        message: 'QuickBooks Desktop connector paired. Add a company file from the Windows tray.',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to pair QuickBooks Desktop connector',
        type: 'error',
      });
    },
  });
};
